import { LitElement, html, css, nothing } from 'lit';
import { resolveHref, localeFromPathname } from './resolve-href.js';
import { navData } from './data/nav-data.js';
import { getCopy, resolveCopy, resolveItemCopy } from './i18n/copy.js';

// Builds the pmt click-tracking id (www-next's SCALP click tracker keys off
// `data-track-entity` / `pm-analytics`) from human-readable path segments —
// dropdown key, column name, item title — rather than from `key` (the
// locale-translated global.json path), which pmt never reads. Mirrors
// www-next's NavDropdownMenu.jsx toSnakeSegment/toTrackEntity exactly, so the
// two components produce the same ids for the same menu.
export const toSnakeSegment = (value) =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

export const toTrackEntity = (...segments) =>
  `navigation.header.${segments.filter(Boolean).map(toSnakeSegment).join('.')}.click`;

// <postman-navbar> — interactive mega-menu.
//
// - Dropdown triggers are real <button>s with aria-expanded / aria-haspopup /
//   aria-controls; one panel open at a time.
// - Escape closes the open panel and returns focus to its trigger.
// - Pointer-down outside the component closes any open panel.
// - Below the mobile breakpoint (1150px, www.postman.com's own) a hamburger
//   toggles a drawer that slides down over the page from under the bar; inside
//   it, dropdown panels expand inline (stacked) instead of floating.
// - Links resolve through resolveHref for the current `host`; clicks emit a
//   `postman-nav:click` event for the host to forward to its analytics, and
//   carry a `data-track-entity` attribute (no `data-cdp`) so a host's own
//   document-level pmt/SCALP listener can pick them up via composedPath.
// - Host-specific controls go in the `logo` / `search` / `account` / `local-nav`
//   slots, plus `account-mobile` / `local-nav-mobile` for the hamburger drawer.
//   `account` and `local-nav` sit in the top bar, which has no room on a phone,
//   and a named slot projects only once — so hosts fill both and the component
//   shows one per breakpoint. `search` is the exception: it stays in the bar at
//   every width, beside the hamburger.
export class PostmanNavbar extends LitElement {
  static properties = {
    host: { type: String },
    locale: { type: String },
    signedIn: { type: Boolean, attribute: 'signed-in' },
    // String (not Boolean): default-ON with an explicit `translate="false"`
    // opt-out. A Boolean attribute keys off presence, so `="false"` would read
    // as true — the opposite of what a suppress flag needs. Same shape as
    // <postman-footer>'s emit-jsonld.
    translate: { type: String },
    // `theme="dark"` is the one value implemented today (mirrors the redesigned
    // www.postman.com homepage's dark nav). Anything else, including no
    // attribute, renders the existing light navbar. Deliberately NOT derived
    // from the pathname the way `locale` is: "is this the dark homepage" is a
    // per-property product decision (www-next's own `isDarkHomepage()` scopes
    // it to '/', '/home/', and the locale roots), not something this
    // multi-consumer component can infer — a Learning Center or blog root is
    // not the redesigned homepage. The consumer declares it, same reasoning as
    // `translate`.
    //
    // `reflect: true` because the dark styling below is entirely
    // `:host([theme='dark'])` attribute selectors — Lit does not reflect a
    // property to its attribute by default, so `navbar.theme = 'dark'` would
    // otherwise update this.theme (and the JS-read mobile wordmark swap)
    // without ever activating the CSS, and leave stale dark styles behind if
    // reset to light.
    theme: { type: String, reflect: true },
    _open: { state: true },
    _mobileOpen: { state: true },
    _activeIndex: { state: true },
  };

  static styles = css`
    /* Design tokens match www.postman.com's GlobalNavbar (Inter, white bar,
       54px tall, grey→#212121 triggers, white rounded mega-menu panel). */
    :host {
      display: block;
      font-family: var(--pm-font, 'Inter', system-ui, -apple-system, sans-serif);
    }
    nav {
      position: sticky;
      top: 0;
      z-index: 55;
      display: flex;
      align-items: center;
      box-sizing: border-box;
      height: 54px;
      background: var(--pm-nav-bg, #fff);
      border-bottom: 1px solid var(--pm-nav-border, #e6e6e6);
      /* px, not the source's 0.8rem/1.6rem: www.postman.com sets
         html{font-size:62.5%}, so its rem values compute to 8px/16px. A
         component that ships to any host cannot borrow the host's root font
         size — on a default 16px root the same rems would render 12.8/25.6px
         and the 30px hamburger would no longer fit the 54px bar. */
      padding: 8px 16px;
      /* www.postman.com sets letter-spacing:-0.16px on the BODY (global.css), so
         every string in the bar and the mega-menu inherits it. Left at normal,
         a 12px description measures wide enough to take an extra line: the AI
         Engineer card stood 105px tall against the site's 86px, and its subtitle
         broke after "with" instead of "agents.". The bar's triggers opt back out
         below (the source sets tracking-normal on them), so this lands on the
         panel, the drawer and the slot fallbacks — the live scope. */
      letter-spacing: var(--pm-letter-spacing, -0.16px);
    }
    /* Bar layout: logo + menubar pinned left, host controls + hamburger right
       (source: "flex w-full items-center justify-between"). */
    .bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      min-width: 0;
    }
    .bar-left,
    .bar-right {
      display: flex;
      align-items: center;
      min-width: 0;
    }
    /* Gap between the host's "search" control and the hamburger. Collapses on
       desktop, where the hamburger is display:none. */
    .bar-right {
      gap: 8px;
    }
    /* ONE node, two roles. Above the breakpoint .drawer/.drawer-scroll are inert
       flex wrappers that leave the menubar sitting in the bar; below it, the same
       node becomes the sliding overlay drawer (see the mobile block). The
       alternative — a second copy of the menu markup for the drawer, which is
       what the React source does — would duplicate every id in the mega-menu and
       double the SSR payload. */
    .drawer,
    .drawer-scroll {
      display: flex;
      align-items: center;
      min-width: 0;
    }
    /* Default account controls (slot fallback). Plain shadow CSS, no utility
       classes: Fern's custom-header compiler silently no-ops Tailwind arbitrary
       values and screen variants, so anything relying on them would compute to
       defaults there. */
    .acct-group {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
    }
    /* In the drawer the same buttons stay a ROW, as they do on the marketing
       site ("mr-[0.4rem]" between them) — but wrapping, so three localized
       labels can never scroll a 390px phone sideways. */
    .acct-group.acct-mobile {
      flex-wrap: wrap;
      gap: 4px;
    }
    .acct {
      font: inherit;
      font-size: 0.85rem;
      font-weight: 600;
      line-height: 1;
      padding: 0.5rem 0.8rem;
      border-radius: 4px;
      text-decoration: none;
      white-space: nowrap;
      border: 1px solid transparent;
    }
    .acct-secondary {
      color: var(--pm-nav-fg, #212121);
      border-color: var(--pm-nav-border, #e6e6e6);
    }
    .acct-secondary:hover {
      border-color: var(--pm-nav-fg, #212121);
    }
    .acct-primary {
      background: var(--pm-nav-accent, #ff6c37);
      color: #fff;
    }
    .acct-primary:hover {
      background: var(--pm-nav-accent-hover, #e35c2c);
    }
    .acct:focus-visible {
      outline: 2px solid var(--pm-nav-accent, #ff6c37);
      outline-offset: 2px;
    }

    /* Default logo (slot fallback). A host that projects its own into
       slot="logo" replaces this entirely, so these rules stop applying — style
       the projected element from the host page instead. */
    .logo {
      display: inline-flex;
      align-items: center;
      flex: none;
      /* Measured off the live bar (its mr-1 computes to 10px there), and in px
         for the same reason as the bar padding. At 1.6rem the first trigger sat
         16px right of where www.postman.com puts it. */
      margin-right: 10px;
      border: 0;
      text-decoration: none;
    }
    .logo img {
      display: block;
    }
    .logo-mark {
      width: 32px;
      height: 32px;
    }
    /* Dark-theme-only (see _logo()); hidden by default so a light-theme host
       never reserves layout for it. Shown below the mobile breakpoint only
       when theme="dark" — see the dark-theme block below. */
    .logo-wordmark {
      display: none;
      width: 94px;
      height: 28px;
    }
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .menubar {
      display: flex;
      align-items: center;
      gap: 0;
    }
    .trigger,
    .top-link {
      font: inherit;
      font-size: 14px;
      font-weight: 600;
      line-height: 21px;
      letter-spacing: normal;
      color: var(--pm-nav-link, #6b6b6b);
      background: none;
      border: 0;
      padding: 0 15px;
      cursor: pointer;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      /* Space before the chevron: the source's ml-[0.5rem] on the chevron
         wrapper, which computes to 5px there. At 0.5rem (8px here) every
         dropdown trigger came out 3px wide, pushing the whole bar right. */
      gap: 5px;
      white-space: nowrap;
    }
    .trigger[aria-expanded='true'],
    .trigger:hover,
    .top-link:hover {
      color: var(--pm-nav-link-hover, #212121);
    }
    .chevron {
      width: 12px;
      height: 12px;
      transition: transform 0.2s ease-in-out;
      transform: scale(0.85);
    }
    .trigger[aria-expanded='true'] .chevron {
      transform: rotate(180deg) scale(0.85);
    }
    :focus-visible {
      outline: 2px solid var(--pm-focus-ring, #ff6c37);
      outline-offset: 2px;
    }
    .item {
      position: relative;
      /* Source wraps each top-level item in mx-[8px]; combined with the
         trigger's 15px padding this gives the bar its item spacing. */
      margin: 0 8px;
    }
    /* A closed panel is [hidden]. The UA display:none must win over the
       author display:flex on .panel.has-brands (same specificity), or the
       Product dropdown stays visible on load. */
    .panel[hidden] {
      display: none !important;
    }
    .panel {
      position: absolute;
      top: 100%;
      left: -14px;
      /* Never wider than the viewport. Belt to the braces of the runtime shift
         below: if a panel is so wide that shifting cannot fit it, it wraps
         instead of hanging off an edge. */
      max-width: calc(100vw - 2 * var(--pm-panel-gutter, 16px));
      z-index: 2147483647;
      /* 8px, the source's 0.8rem at its 62.5% root. */
      margin-top: 8px;
      width: max-content;
      box-sizing: border-box;
      background: var(--pm-panel-bg, #fff);
      border: 1px solid var(--pm-panel-border, #e6e6e6);
      border-radius: 10px;
      box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
      padding: 20px;
    }
    /* Source has NO gap between columns: spacing comes from fixed column
       widths + each item's 10px padding. */
    .columns {
      display: flex;
      gap: 0;
      align-items: flex-start;
    }
    .col {
      box-sizing: border-box;
    }
    .col.w210 {
      width: 210px;
    }
    .col.w180 {
      width: 180px;
      /* The source's xl:mb-[20px] on the Resources columns. */
      margin-bottom: 20px;
    }
    /* Product's Platform/Explore column: narrower, rendered last, with a
       20px gap (margin) + divider separating it from the icon columns. */
    .col.left-nav {
      width: 190px;
      margin-left: 20px;
      border-left: 1px solid #e6e6e6;
      padding-left: 20px;
    }
    /* Solutions rows stretch to the grid row, so the hover background covers the
       whole cell rather than just a one-line item beside a two-line one — the
       source's items are the grid children themselves. */
    .solutions-grid > li {
      display: flex;
    }
    .solutions-grid .menu-item {
      flex: 1;
      box-sizing: border-box;
      /* Undo .menu-item's align-items:center HERE, and only here. That centring
         is right for the icon columns, where the row is exactly as tall as its
         one line of content — but a Solutions cell is stretched to the tallest
         cell in its row, so centring drops the content to the middle of the
         stretched box. "API Governance" sits beside a two-line description and
         was the only item this showed on: its title landed 19.75px below its
         cell against the site's 10px, ~10px lower than "Partner API
         Management" next to it. The other three wrap to two lines, so they fill
         their cells and centring is a no-op — which is what hid it.
         The source never has to do this because the hover background and the
         flex box are DIFFERENT elements there: the wrapper div stretches and
         carries the background, while the anchor inside it is block-level and
         so starts at the top. Here .menu-item is both. */
      align-items: flex-start;
    }
    /* Solutions: all items in one 2-column grid, equal-height rows. */
    .solutions-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-auto-rows: 1fr;
      list-style: none;
      margin: 0;
      padding: 0;
      width: 410px;
      max-width: 100%;
    }
    .col-title,
    .section-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 0 0 10px 10px;
      font-family: var(--pm-mono, 'IBM Plex Mono', ui-monospace, 'SFMono-Regular', monospace);
      font-size: 12px;
      line-height: var(--pm-panel-line-height, 1.625);
      font-weight: 400;
      text-transform: uppercase;
      color: var(--pm-nav-section, #e05320);
    }
    .section-header {
      margin-top: 30px;
    }
    /* Grey pill beside a column heading ("TOOLS new"). */
    .col-badge {
      display: inline-block;
      padding: 2px 6px;
      border: 1px solid var(--pm-panel-border, #e6e6e6);
      border-radius: 4px;
      background: #fff;
      font-family: inherit;
      font-size: 10px;
      font-weight: 400;
      line-height: 1;
      text-transform: uppercase;
      color: #707070;
    }
    .menu-item {
      display: flex;
      gap: 8px;
      /* Centred, because these rows are a single line: the icon columns carry
         titles only (see nav-data.js). The two places that DO stack a
         description — featured cards and brands — align to the top instead. */
      align-items: center;
      padding: 10px;
      border-radius: 5px;
      text-decoration: none;
      color: #212121;
      transition: background-color 0.15s ease-in-out;
    }
    .menu-item:hover {
      background: var(--pm-nav-hover-bg, #efefef);
    }
    /* 1.625 is the line-height the panel inherits on www.postman.com — 22.75px
       on a 14px title, 19.5px on a 12px description or column heading. Left to
       "normal" the rows came out 37px against the site's 43px. (The BAR is
       different: its triggers set leading-[21px] explicitly, so those stay 21px.) */
    .menu-item .title {
      display: block;
      font-size: 14px;
      line-height: var(--pm-panel-line-height, 1.625);
      font-weight: 600;
      color: #212121;
    }
    .menu-item .desc {
      display: block;
      font-size: 12px;
      line-height: var(--pm-panel-line-height, 1.625);
      color: #5c5c5c;
    }
    /* Column icons are recolored, not orange to begin with: the art at
       voyager.postman.com is multi-colored, and the live menu flattens it to
       black and re-tints it to the brand orange. Same filter chain as the
       source's .navIcon, with its empty saturate() given the value it meant —
       an argument-less filter function is invalid CSS, which would drop the
       whole declaration and leave the icons raw.
       scale(0.8) is the source's background-size:80%: the box stays 22px, so
       every title still starts at the same x, but the glyph is drawn smaller. */
    .menu-item img {
      width: 22px;
      height: 22px;
      flex-shrink: 0;
      transform: scale(0.8);
      filter: var(
        --pm-nav-icon-filter,
        brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(576%) hue-rotate(331deg)
          brightness(104%)
      );
    }
    /* "See all tools →" — the orange row that closes a column. */
    .menu-item.see-all .title {
      color: var(--pm-nav-section, #e05320);
    }
    /* The ONE panel row that does not take the 1.625 above, and the only one
       whose height is a TWO-FONT line box rather than a single line-height.
       Every other title is a <p> in the source, so global.css's
       "p { line-height: 1.625 }" catches it; the app-cta label is a bare <span>,
       which keeps modern-normalize's "html { line-height: 1.15 }".
       Production measures 39.39px for the row: its wrapper is 16px/1.15 (an
       18.4px strut) and the label is a 14px INLINE span inside it, so baseline
       alignment grows the line box to 19.39px. Both halves are needed — at
       1.625 the row was 42.75px, and as a 14px BLOCK it is 16.1px, i.e. 36.09.
       Either way the Platform column, and with it the whole panel, misses.
       font-size is stated rather than inherited so a consumer's root size can't
       move the strut. */
    .menu-item.app-cta {
      font-size: 16px;
      line-height: var(--pm-panel-cta-line-height, 1.15);
    }
    .menu-item.app-cta .title {
      display: inline;
      color: #0265d2;
      font-weight: 400;
      line-height: var(--pm-panel-cta-line-height, 1.15);
    }
    .menu-item.app-cta:hover {
      background: none;
    }
    /* Featured "AI cards" row: full-width grey cards below the columns. */
    .featured {
      margin-top: 10px;
      padding-top: 20px;
      border-top: 1px solid #e6e6e6;
    }
    .card-row {
      display: flex;
      flex-wrap: nowrap;
      /* The source's xl:gap-2, which computes to 20px. At 8px the four cards
         were 208px instead of 199px, and the AI Engineer card's description
         reflowed from three lines to two — a 9px height difference. */
      gap: 20px;
    }
    .card-row > li {
      flex: 1 1 0;
      min-width: 0;
    }
    .featured .menu-item {
      height: 100%;
      box-sizing: border-box;
      background: #f9f9f9;
      /* Two lines (title + description), so the icon sits with the first one. */
      align-items: flex-start;
    }
    /* The featured cards get the source's OTHER filter (.navIconDark): flattened
       to near-black rather than orange, and drawn full-size in a 20px box
       (background-size:contain there). */
    .featured .menu-item .text {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .featured .menu-item img {
      width: 20px;
      height: 20px;
      margin-top: 1px;
      transform: none;
      filter: var(--pm-nav-icon-filter-dark, brightness(0) invert(12%));
    }
    /* Cards carry an inline gradient; hover keeps it and adds the ring +
       a subtle darken (mirrors the source's ring + brightness hover). */
    .featured .feat-card {
      box-shadow: 0 0 0 0.5px transparent;
      transition:
        box-shadow 0.15s ease-in-out,
        filter 0.15s ease-in-out;
    }
    /* 0.5px, not 1px: the source's hover only swaps the ring COLOUR
       (hover:ring-[#F37FFE]) — its width is fixed by ring-[0.5px] on the card
       itself, so a 1px hover ring was twice the site's. */
    .featured .feat-card:hover {
      background: inherit;
      box-shadow: 0 0 0 0.5px var(--ring, #efefef);
      filter: brightness(0.97);
    }
    /* "More from Postman": grey shell + white inner panel + grey brands
       sidebar, mirroring the live Product mega-menu (NavDropdownMenu). */
    .panel.has-brands {
      display: flex;
      flex-direction: row;
      padding: 0;
      background: var(--pm-brands-bg, #f9f8f7);
    }
    .panel-inner {
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      /* The source's xl:w-[895px], verbatim — an earlier 901px here was arrived
         at by arithmetic that double-counted the padding, and it made the four
         featured cards 10px wider than the site's, which reflows their
         descriptions. Content width 855px holds the grid with room to spare:
         3×210 icon columns + 20px gap + the 190px Platform column (its 20px
         padding and 1px divider are inside that 190px) = 840px. */
      width: 895px;
      background: var(--pm-panel-bg, #fff);
      border-radius: 10px;
      padding: 20px;
      box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
    }
    .brands-panel {
      display: flex;
      flex-direction: column;
      width: 220px;
      flex-shrink: 0;
      box-sizing: border-box;
      padding: 20px;
    }
    .brand-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 10px;
      border-radius: 5px;
      text-decoration: none;
      color: #212121;
      transition: opacity 0.15s ease-in-out;
    }
    .brand-item:hover {
      opacity: 0.8;
    }
    .brand-item img {
      width: 20px;
      height: 20px;
      margin-top: 1px;
      flex-shrink: 0;
    }
    .brand-item .title {
      display: inline-flex;
      align-items: center;
      font-size: 14px;
      line-height: var(--pm-panel-line-height, 1.625);
      font-weight: 600;
      color: #212121;
    }
    /* The brands stack title and description with a gap, unlike the featured
       cards' 4px — the source's gap-1, which is 10px on its spacing scale (the
       same unit as the 10px logo gap and the 20px card gap). */
    .brand-item .text {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .brand-item .desc {
      display: block;
      font-size: 12px;
      line-height: var(--pm-panel-line-height, 1.625);
      color: #5c5c5c;
    }
    .brand-arrow {
      display: inline-flex;
      align-items: center;
      vertical-align: middle;
      margin-left: 5px;
      position: relative;
      top: -1px;
    }
    .brand-arrow .bar {
      height: 1px;
      width: 7px;
      background-color: currentColor;
      transition: width 0.2s ease-in-out;
    }
    .brand-arrow .chev {
      display: block;
      margin-left: -4px;
    }
    .brand-item:hover .brand-arrow .bar,
    .brand-item:focus .brand-arrow .bar {
      width: 12px;
    }
    /* Hamburger — the marketing site's icon to the pixel, in px for the same
       reason as the bar padding (its 3rem/2.4rem/0.2rem assume a 62.5% root).
       The 24px bars in an 18px box is not a mistake to fix: it is what pushes
       them flush with the bar's right padding, which is how they line up with
       the logo's inset on the left. */
    .hamburger {
      display: none;
      font: inherit;
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
      color: inherit;
    }
    .ham-box {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
    }
    .ham-lines {
      position: relative;
      display: block;
      width: 18px;
      height: 24px;
    }
    .ham-bar {
      position: absolute;
      left: 0;
      display: block;
      width: 24px;
      height: 2px;
      border-radius: 2px;
      background: var(--pm-nav-link, #6b6b6b);
      transition:
        transform 0.1s ease-in-out,
        opacity 0.1s ease-in-out;
    }
    .ham-bar:nth-child(1) {
      top: 4px;
    }
    .ham-bar:nth-child(2) {
      top: 12px;
    }
    .ham-bar:nth-child(3) {
      top: 20px;
    }
    /* Open: the outer bars meet in the middle and cross; the middle one fades. */
    .hamburger[aria-expanded='true'] .ham-bar:nth-child(1) {
      transform: translateY(8px) rotate(45deg);
    }
    .hamburger[aria-expanded='true'] .ham-bar:nth-child(2) {
      opacity: 0;
    }
    .hamburger[aria-expanded='true'] .ham-bar:nth-child(3) {
      transform: translateY(-8px) rotate(-45deg);
    }
    .actions {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      min-width: 0;
    }

    /* Host controls for the MOBILE drawer. The "account" and "local-nav" slots
       render in the top bar, which has no room on a phone — and a named slot can
       only be projected once, so the drawer needs its own. Hidden above the
       mobile breakpoint so hosts can supply both without double-rendering.
       (Never use a backtick in this file's CSS comments — the whole block is a
       css tagged template literal, so a stray one terminates it.) */
    .drawer-account,
    .drawer-local {
      display: none;
    }

    /* ---------------------------------------------------------------------
       MOBILE (< 1150px) — the marketing site's drawer.
       1150px is www.postman.com's own switch (its "min-[1150px]:" variants),
       so a visitor crossing between properties sees the swap at the same width.
       --------------------------------------------------------------------- */
    @media (max-width: 1149.98px) {
      .hamburger {
        display: inline-flex;
      }
      /* The bar keeps its 54px: the drawer is an OVERLAY (absolute, under the
         bar), not an extra row, so nothing here wraps or grows. */
      /* Account + local-nav move into the drawer; "search" stays in the bar.
         display:none on the <slot> takes its assigned nodes AND its fallback
         with it, so this needs no cooperation from the host. */
      .actions slot[name='local-nav'],
      .actions slot[name='account'] {
        display: none;
      }
      .drawer {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 60;
        display: block;
        overflow: hidden;
        background: var(--pm-nav-bg, #fff);
        max-height: 100vh;
        opacity: 1;
        /* visibility, not [hidden]/inert, is what keeps the closed drawer out of
           the tab order and the a11y tree: the same node holds the desktop
           menubar, so anything driven off _mobileOpen alone would hide the
           menubar for every desktop visitor too. Delay it on close so the
           collapse is visible, drop the delay on open. */
        visibility: visible;
        pointer-events: auto;
        transition:
          max-height 0.3s ease-in-out,
          opacity 0.3s ease-in-out,
          visibility 0s linear 0s;
      }
      .drawer[data-open='false'] {
        max-height: 0;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition:
          max-height 0.3s ease-in-out,
          opacity 0.3s ease-in-out,
          visibility 0s linear 0.3s;
      }
      .drawer:focus-visible {
        outline: none;
      }
      .drawer-scroll {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        box-sizing: border-box;
        height: calc(100vh - 54px);
        overflow-y: auto;
        padding-top: 16px;
      }
      .menubar {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        box-sizing: border-box;
        width: 100%;
        padding: 10px;
      }
      .item {
        margin: 0 8px 10px;
      }
      /* The 15px side padding is an xl-only rule in the source. */
      .trigger,
      .top-link {
        padding: 0;
      }
      .drawer-account {
        display: block;
        margin: 10px 0;
        padding: 3px 0 0 16px;
      }
      .drawer-local {
        display: block;
        margin: 10px 0;
        padding-left: 16px;
      }
      /* Accordion. A closed panel collapses instead of going display:none, so it
         can animate like the drawer; visibility keeps its links untabbable and
         unannounced while collapsed. */
      .panel {
        position: static;
        width: auto;
        max-width: none;
        margin-top: 8px;
        box-shadow: none;
        border: 0;
        border-radius: 0;
        padding: 10px;
        overflow: hidden;
        /* 300vh is the source's value. The floor is not: below ~560px of
           viewport height (any phone in landscape, still under the breakpoint)
           300vh is shorter than the stacked Product panel, and max-height
           CLIPS — the source silently loses its bottom rows there. */
        max-height: max(300vh, 2400px);
        opacity: 1;
        visibility: visible;
        transition:
          max-height 0.3s ease-in-out,
          padding 0.3s ease-in-out,
          margin-top 0.3s ease-in-out,
          opacity 0.3s ease-in-out,
          visibility 0s linear 0s;
      }
      .panel[hidden] {
        /* Beats the base rule's display:none (same specificity, both important —
           this one is later). Per-display-type so the Product panel does not
           flip block/flex mid-collapse. */
        display: block !important;
        max-height: 0;
        /* A border-box height can never go below its own padding, so max-height:0
           alone leaves every collapsed panel 20px tall — which is what spaced the
           dropdown rows 28px further apart than the plain links. The source gets
           this for free by putting the padding on a separate inner element. */
        padding-top: 0;
        padding-bottom: 0;
        margin-top: 0;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition:
          max-height 0.3s ease-in-out,
          padding 0.3s ease-in-out,
          margin-top 0.3s ease-in-out,
          opacity 0.3s ease-in-out,
          visibility 0s linear 0.3s;
      }
      /* Product only. Its .panel.has-brands rule below re-declares padding at the
         same specificity as .panel[hidden] and comes later, so the collapse has
         to be restated here to win. */
      .panel.has-brands[hidden] {
        display: flex !important;
        padding-top: 0;
        padding-bottom: 0;
        margin-top: 0;
      }
      .columns {
        flex-direction: column;
        gap: 0;
      }
      /* Product's Platform/Explore column leads on mobile with a rule UNDER it
         (the source's "border-b" / "xl:order-last xl:border-l"), so undo the
         desktop left divider and pull it back to the front. */
      .col.left-nav {
        order: -1;
        width: auto;
        margin-left: 0;
        border-left: 0;
        border-bottom: 1px solid #e6e6e6;
        padding-left: 20px;
        padding-bottom: 15px;
      }
      .col.w210 {
        width: auto;
        padding: 30px 0;
      }
      /* "Download Postman →" is a bottom-anchored CTA in the desktop Platform
         column (mt-auto); the source hides it outside that layout rather than
         leaving a stray blue link in the drawer. */
      .menu-item.app-cta {
        display: none;
      }
      .col.w180 {
        width: auto;
      }
      /* One column per row: two columns of 14px titles + descriptions do not fit
         a phone (the source drops to grid-cols-1 below lg for the same reason). */
      .solutions-grid {
        grid-template-columns: 1fr;
        width: auto;
      }
      .card-row {
        flex-direction: column;
        gap: 0;
      }
      /* The featured cards lose their grey/gradient fill on mobile (the source
         hides the gradient layer below xl). The gradient is an inline style, so
         only !important can take it back off. */
      .featured .menu-item,
      .featured .feat-card {
        background: none !important;
        box-shadow: none;
      }
      /* Stack the brands sidebar below the columns on small screens. */
      .panel.has-brands {
        flex-direction: column;
        background: none;
        padding: 10px;
      }
      .panel-inner {
        width: auto;
        background: none;
        box-shadow: none;
        border-radius: 0;
        padding: 0;
      }
      .brands-panel {
        width: auto;
        padding: 20px 0 0;
        margin-top: 10px;
        border-top: 1px solid #e6e6e6;
      }
    }

    /* Respect a reduced-motion preference: the drawer and its accordions still
       open and close, they just do it instantly. */
    @media (prefers-reduced-motion: reduce) {
      .drawer,
      .drawer[data-open='false'],
      .panel,
      .panel[hidden],
      .chevron,
      .trigger[aria-expanded='true'] .chevron,
      .ham-bar {
        transition: none;
      }
    }

    /* ---------------------------------------------------------------------
       DARK THEME (theme="dark") — the redesigned www.postman.com homepage's
       nav treatment, ported from GlobalNavbar.jsx/NavDropdownMenu.jsx's
       dark: variants. Scoping this to '/' and '/home/' is the CONSUMER's
       job (see the theme property doc above); this block only implements
       what the attribute looks like once set.

       Where a rule below already reads a --pm-nav-*/--pm-panel-*/--pm-brands-*
       custom property, redefining the property here is enough — every rule
       using it anywhere above re-resolves automatically. Rules with hardcoded
       colors (menu-item text, the account buttons, the hamburger bars, the
       featured-card icon filter) need an explicit override instead, since
       there is no shared token they can be redirected through without also
       affecting the light theme.
       --------------------------------------------------------------------- */
    :host([theme='dark']) {
      --pm-nav-bg: #0e0704;
      --pm-nav-border: #2a2018;
      --pm-nav-link: #8a8178;
      --pm-nav-link-hover: #fff;
      --pm-nav-fg: #cdbfad;
      --pm-nav-section: #ff8e64;
      --pm-nav-hover-bg: #241811;
      --pm-panel-bg: #17100a;
      --pm-panel-border: #2a2018;
      --pm-brands-bg: #221710;
      /* The featured cards' near-black icon tint (navIconDark) disappears
         against a dark panel; invert it light instead, same as the site. */
      --pm-nav-icon-filter-dark: brightness(0) invert(85%);
    }
    :host([theme='dark']) .menu-item .title,
    :host([theme='dark']) .menu-item .desc,
    :host([theme='dark']) .brand-item .title,
    :host([theme='dark']) .brand-item .desc {
      color: var(--pm-nav-fg, #cdbfad);
    }
    :host([theme='dark']) .menu-item.see-all .title {
      color: var(--pm-nav-section, #ff8e64);
    }
    :host([theme='dark']) .menu-item.app-cta .title {
      color: #5aa2ff;
    }
    :host([theme='dark']) .col-badge {
      background: #241811;
      border-color: #3a2c22;
      color: var(--pm-nav-fg, #cdbfad);
    }
    :host([theme='dark']) .col.left-nav {
      /* Desktop uses border-left; the mobile media query below swaps it for
         border-bottom (source: "border-b" / "xl:border-l") — override both
         sides so the drawer doesn't keep a light-theme #e6e6e6 divider. */
      border-left-color: #2a2018;
      border-bottom-color: #2a2018;
    }
    :host([theme='dark']) .featured {
      border-top-color: #2a2018;
    }
    :host([theme='dark']) .brands-panel {
      /* Only the mobile media query below gives .brands-panel a border-top;
         harmless no-op at desktop where it has none. */
      border-top-color: #2a2018;
    }
    :host([theme='dark']) .featured .menu-item {
      background: #1e1e1e;
    }
    /* Hamburger bars use the warm-grey fg token, not --pm-nav-link: the site
       keys these two off different shades (#cdbfad vs #8a8178). */
    :host([theme='dark']) .ham-bar {
      background: var(--pm-nav-fg, #cdbfad);
    }
    /* Account buttons: GenericButton's secondaryGhost variant — transparent,
       warm-grey border/text, inverts toward a warm white on hover. The filled
       light-grey default would read as a pale block on the near-black bar. */
    :host([theme='dark']) .acct-secondary {
      color: var(--pm-nav-fg, #cdbfad);
      border-color: var(--pm-nav-fg, #cdbfad);
    }
    :host([theme='dark']) .acct-secondary:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: #fff0de;
      color: #fff0de;
    }
    @media (max-width: 1149.98px) {
      :host([theme='dark']) .logo-mark {
        display: none;
      }
      :host([theme='dark']) .logo-wordmark {
        display: block;
      }
    }
  `;

  constructor() {
    super();
    this.locale = 'en';
    this.signedIn = false;
    this._open = '';
    this._mobileOpen = false;
    // Roving-tabindex index into the menubar's top-level items (navData order):
    // only the active item is tabbable; Arrow keys move focus between them.
    this._activeIndex = 0;
    this._onDocPointer = (e) => {
      if (this._open && !e.composedPath().includes(this)) this._open = '';
    };
    // Escape from anywhere on the page, matching the marketing site's two
    // document-level listeners — except that theirs both fire and tear down the
    // dropdown AND the drawer in one keystroke. Dismiss one layer at a time
    // instead: a visitor collapsing a section rarely means "close the menu".
    // Skips events the menubar/panel handlers already consumed.
    this._onDocKeydown = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (this._open) {
        this._open = '';
        return;
      }
      if (this._mobileOpen) this._closeDrawer({ focusToggle: true });
    };
    // Back/forward within a single-page host: the drawer is over the new page.
    this._onPopState = () => this._closeDrawer();
    // Crossing above the breakpoint hides the drawer in CSS but would leave the
    // body scroll-locked with no visible control to release it (the live site
    // has exactly this bug). Close it properly instead.
    this._onViewportChange = (e) => {
      if (e.matches) this._closeDrawer();
    };
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('pointerdown', this._onDocPointer);
    document.addEventListener('keydown', this._onDocKeydown);
    window.addEventListener('popstate', this._onPopState);
    // Kept in sync with the breakpoint in `styles` by hand — Lit's css literal
    // cannot interpolate a JS constant into a media query.
    this._desktopQuery = window.matchMedia?.('(min-width: 1150px)');
    this._desktopQuery?.addEventListener?.('change', this._onViewportChange);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this._onDocPointer);
    document.removeEventListener('keydown', this._onDocKeydown);
    window.removeEventListener('popstate', this._onPopState);
    this._desktopQuery?.removeEventListener?.('change', this._onViewportChange);
    // Never leave a host page unscrollable because the element was torn down
    // (a route change in an SPA host) while the drawer was open.
    this._unlockScroll();
    super.disconnectedCallback();
  }

  // --- Mobile drawer -------------------------------------------------------

  _toggleDrawer() {
    if (this._mobileOpen) this._closeDrawer({ focusToggle: true });
    else this._openDrawer();
  }

  _openDrawer() {
    this._mobileOpen = true;
    this._lockScroll();
    // The site focuses its panel on open, which is what lets Escape and Tab
    // start inside the drawer rather than back at the top of the document.
    this.updateComplete.then(() => this.renderRoot.querySelector('#mobile-panel')?.focus());
  }

  _closeDrawer({ focusToggle = false } = {}) {
    if (!this._mobileOpen) return;
    this._mobileOpen = false;
    this._unlockScroll();
    if (focusToggle) {
      this.updateComplete.then(() => this.renderRoot.querySelector('.hamburger')?.focus());
    }
  }

  // Following a link closes the drawer: on a host that navigates client-side
  // (or to a #hash on the same page) nothing else would, and the drawer covers
  // the destination. Uses composedPath so links a host slots into
  // account-mobile / local-nav-mobile count too.
  _onDrawerClick(e) {
    if (!this._mobileOpen) return;
    const link = e.composedPath().find((n) => n?.tagName === 'A' && n.hasAttribute?.('href'));
    if (link) this._closeDrawer();
  }

  // `search` is the one host control that shares the bar with the hamburger, so
  // the two compete for the same screen: activating search with the drawer open
  // would put the host's field BEHIND a full-height overlay, and move focus into
  // something invisible. Whatever search opens, the drawer is in its way.
  _onBarControlsClick() {
    this._closeDrawer();
  }

  // Body scroll lock, as the marketing site does it: the drawer is a full-height
  // overlay, and a page scrolling behind it reads as broken.
  _lockScroll() {
    if (typeof document === 'undefined' || this._prevBodyOverflow != null) return;
    this._prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  _unlockScroll() {
    if (typeof document === 'undefined' || this._prevBodyOverflow == null) return;
    document.body.style.overflow = this._prevBodyOverflow;
    this._prevBodyOverflow = null;
  }

  /**
   * Locale actually used for copy and link prefixing.
   *
   * An explicit `locale` attribute always wins. With none, it is derived from
   * the URL — mirroring how `host` falls back to location.origin, so a
   * script-tag consumer gets sensible behaviour with zero configuration.
   *
   * This matters for hosts with no router to ask: Fern's custom-header compiler
   * forbids imports and exposes no usePathname, so the Learning Center would
   * otherwise hand-roll the prefix map — and `fr-CA` maps to `fr-ca`, not `fr`,
   * which this repo itself got wrong until v1.0.3.
   */
  /**
   * Is this host localized at all?
   *
   * `translate="false"` is a FULL opt-out: English copy AND no locale prefixing
   * on links. It exists because being localized is a per-property fact, not
   * something the component can infer — the blog, for instance, ships only a
   * .pot template and has no translated content, so a Japanese navbar above
   * English posts would be worse than an English one.
   *
   * Deliberately consumer-declared rather than a host registry here: `host`
   * accepts any origin by contract, each property's translation readiness moves
   * on its own schedule, and the component cannot verify a claim like "the blog
   * has JA content now". A registry would rot; an attribute shows up in the
   * consumer's own diff.
   */
  get translationEnabled() {
    return String(this.translate ?? '').toLowerCase() !== 'false';
  }

  get effectiveLocale() {
    if (this.hasAttribute('locale')) return this.locale;
    return localeFromPathname() ?? this.locale;
  }

  get ctx() {
    const origin = this.host || (typeof location !== 'undefined' ? location.origin : '');
    const on = this.translationEnabled;
    // localizesPages is what resolveHref gates prefixing on, so switching it off
    // is what makes this a FULL opt-out rather than copy-only: an opted-out host
    // links to unprefixed pages instead of /jp/ or /es/.
    return { origin, locale: on ? this.effectiveLocale : 'en', localizesPages: on };
  }

  // Copy bundle for the active locale (null for en → inline English source).
  get _copyMap() {
    return this.translationEnabled ? getCopy(this.effectiveLocale) : null;
  }

  // Localize a leaf label (menu/column heading) by its `key`, falling back to
  // the inline English `title`.
  _t(node) {
    return resolveCopy(node.key, node.title, this._copyMap);
  }

  _toggle(id) {
    this._open = this._open === id ? '' : id;
  }

  // Top-level menubar items in navData order (one per <li>). Roving focus and
  // tabindex move across these; only the active one is in the tab sequence.
  get _menubarItems() {
    return [
      ...this.renderRoot.querySelectorAll('#menubar > li > .trigger, #menubar > li > .top-link'),
    ];
  }

  _focusMenubarItem(index) {
    const items = this._menubarItems;
    if (!items.length) return;
    const i = (index + items.length) % items.length;
    this._activeIndex = i;
    items[i].focus();
  }

  // Menubar-level keyboard nav (WCAG APG menubar pattern): Left/Right/Home/End
  // roam the top-level items; ArrowDown opens a dropdown and enters its panel;
  // Escape closes an open panel while focus stays on its trigger.
  _onMenubarKeydown(e) {
    const items = this._menubarItems;
    const current = items.indexOf(e.target);
    if (current === -1) return; // event bubbled up from inside a panel

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        this._focusMenubarItem(current + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this._focusMenubarItem(current - 1);
        break;
      case 'Home':
        e.preventDefault();
        this._focusMenubarItem(0);
        break;
      case 'End':
        e.preventDefault();
        this._focusMenubarItem(items.length - 1);
        break;
      case 'ArrowDown':
        if (e.target.classList.contains('trigger')) {
          e.preventDefault();
          const id = e.target.id.replace('trigger-', '');
          this._open = id;
          this.updateComplete.then(() => {
            this.renderRoot.querySelector(`#panel-${id} a[href]`)?.focus();
          });
        }
        break;
      case 'Escape':
        if (this._open) {
          e.preventDefault();
          this._open = '';
        }
        break;
    }
  }

  // Panel-level keyboard nav: Up/Down/Home/End move between the panel's links;
  // ArrowUp from the first link returns to the trigger; Escape closes and
  // returns focus to the trigger.
  _onPanelKeydown(e, id) {
    const trigger = () => this.renderRoot.querySelector(`#trigger-${id}`);
    if (e.key === 'Escape') {
      e.preventDefault();
      this._open = '';
      this.updateComplete.then(() => trigger()?.focus());
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const links = [...this.renderRoot.querySelectorAll(`#panel-${id} a[href]`)];
    if (!links.length) return;
    e.preventDefault();
    const cur = links.indexOf(e.target);
    if (e.key === 'Home') return links[0].focus();
    if (e.key === 'End') return links[links.length - 1].focus();
    if (e.key === 'ArrowDown') return (links[cur + 1] ?? links[0]).focus();
    // ArrowUp: step back, or leave the panel for the trigger at the top.
    if (cur <= 0) return trigger()?.focus();
    links[cur - 1].focus();
  }

  _emitClick(item) {
    // Analytics contract: hosts subscribe and forward to their own SDK.
    this.dispatchEvent(
      new CustomEvent('postman-nav:click', {
        detail: { id: item.id, title: item.title },
        bubbles: true,
        composed: true,
      })
    );
  }

  _link(item, cls, index) {
    const target = item.link?.target;
    return html`<a
      class=${cls}
      part="nav-link"
      role="menuitem"
      tabindex=${index === this._activeIndex ? '0' : '-1'}
      href=${resolveHref(item.link, this.ctx)}
      target=${target ?? nothing}
      rel=${target === '_blank' ? 'noopener noreferrer' : nothing}
      data-track-entity=${`navigation.header.nav_link.${item.id}.click`}
      @click=${() => this._emitClick(item)}
      >${this._t(item)}</a
    >`;
  }

  _menuItem(item, opts = {}) {
    const copy = resolveItemCopy(item, this._copyMap);
    if (item.style === 'section-header') {
      return html`<li class="section-header" role="presentation">${copy.title}</li>`;
    }
    const target = item.link?.target;
    const card = opts.card === true;
    // Featured cards carry an inline gradient + a per-card hover-ring color.
    const style =
      card && item.bgStyle
        ? `background:${item.bgStyle}${item.hoverRing ? `;--ring:${item.hoverRing}` : ''}`
        : nothing;
    return html`<li>
      <a
        class="menu-item ${item.style === 'app-cta' ? 'app-cta' : ''} ${
          item.style === 'see-all' ? 'see-all' : ''
        } ${card ? 'feat-card' : ''}"
        part="nav-link"
        style=${style}
        href=${resolveHref(item.link, this.ctx)}
        target=${target ?? nothing}
        rel=${target === '_blank' ? 'noopener noreferrer' : nothing}
        data-track-entity=${opts.trackEntity ?? nothing}
        @click=${() => this._emitClick(item)}
      >
        ${item.icon ? html`<img src=${item.icon} alt="" aria-hidden="true" />` : nothing}
        <span class="text">
          <span class="title">${copy.title}</span>
          ${copy.description ? html`<span class="desc">${copy.description}</span>` : nothing}
        </span>
      </a>
    </li>`;
  }

  // One dropdown column: an optional orange title + its items.
  _column(col, extraClass = '', dropdownKey) {
    const title = this._t(col);
    return html`<div class="col ${extraClass}">
      ${
        title
          ? html`<p class="col-title">
              ${title}${
                col.badge ? html`<span class="col-badge" part="badge">${col.badge}</span>` : nothing
              }
            </p>`
          : nothing
      }
      <ul>
        ${col.items.map((it) =>
          this._menuItem(it, { trackEntity: toTrackEntity(dropdownKey, col.title, it.title) })
        )}
      </ul>
    </div>`;
  }

  _brandItem(item, dropdownKey) {
    const target = item.link?.target;
    const copy = resolveItemCopy(item, this._copyMap);
    return html`<a
      class="brand-item"
      part="nav-link"
      href=${resolveHref(item.link, this.ctx)}
      target=${target ?? nothing}
      rel=${target === '_blank' ? 'noopener noreferrer' : nothing}
      data-track-entity=${toTrackEntity(dropdownKey, 'brands', item.title)}
      @click=${() => this._emitClick(item)}
    >
      ${item.icon ? html`<img src=${item.icon} alt="" aria-hidden="true" />` : nothing}
      <span class="text">
        <span class="title"
          >${copy.title}
          <span class="brand-arrow" aria-hidden="true">
            <span class="bar"></span>
            <svg
              class="chev"
              xmlns="http://www.w3.org/2000/svg"
              width="5"
              height="9"
              viewBox="9 0 5 8"
              fill="currentColor"
            >
              <path
                d="M13.3536 4.03553C13.5488 3.84027 13.5488 3.52369 13.3536 3.32843L10.1716 0.146447C9.97631 -0.0488155 9.65973 -0.0488155 9.46447 0.146447C9.2692 0.341709 9.2692 0.658291 9.46447 0.853554L12.2929 3.68198L9.46447 6.51041C9.2692 6.70567 9.2692 7.02225 9.46447 7.21751C9.65973 7.41278 9.97631 7.41278 10.1716 7.21751L13.3536 4.03553Z"
              ></path>
            </svg>
          </span>
        </span>
        ${copy.description ? html`<span class="desc">${copy.description}</span>` : nothing}
      </span>
    </a>`;
  }

  _panel(menu) {
    const open = this._open === menu.id;
    const hasBrands = menu.brands?.length > 0;

    // Per-menu layout, mirroring the source NavDropdownMenu:
    //  - solutions: flatten every column's items into one 2-col grid.
    //  - product:  the first column (Platform/Explore) is a narrower "left nav"
    //              rendered LAST with a divider, after the other columns.
    //  - default (resources): even columns with orange titles.
    let columnsBlock;
    if (menu.id === 'solutions') {
      const items = menu.columns.flatMap((c) => c.items);
      columnsBlock = html`<ul class="solutions-grid">
        ${items.map((it) =>
          this._menuItem(it, { trackEntity: toTrackEntity(menu.id, 'Solutions', it.title) })
        )}
      </ul>`;
    } else if (menu.id === 'product' && menu.columns.length > 1) {
      const [platform, ...rest] = menu.columns;
      columnsBlock = html`<div class="columns">
        ${rest.map((col) => this._column(col, 'w210', menu.id))}${this._column(
          platform,
          'left-nav',
          menu.id
        )}
      </div>`;
    } else {
      // Resources columns are 180px in the source; other multi-column menus
      // fall back to natural width.
      const w = menu.id === 'resources' ? 'w180' : '';
      columnsBlock = html`<div class="columns">
        ${menu.columns.map((col) => this._column(col, w, menu.id))}
      </div>`;
    }

    // On the live site this content sits in a white inner panel; the brands
    // sidebar renders beside it.
    const content = html`
      ${columnsBlock}
      ${
        menu.featured
          ? html`<div class="featured">
              <ul class="card-row">
                ${menu.featured.map((it) =>
                  this._menuItem(it, {
                    card: true,
                    trackEntity: toTrackEntity(menu.id, 'featured', it.title),
                  })
                )}
              </ul>
            </div>`
          : nothing
      }
    `;

    return html`<div
      id="panel-${menu.id}"
      part="panel"
      class="panel ${hasBrands ? 'has-brands' : ''}"
      role="region"
      aria-label=${this._t(menu)}
      ?hidden=${!open}
      @keydown=${(e) => this._onPanelKeydown(e, menu.id)}
    >
      ${
        hasBrands
          ? html`
              <div class="panel-inner">${content}</div>
              <aside class="brands-panel" part="brands">
                <p class="col-title">${this._t(menu.brandsHeading ?? {})}</p>
                ${menu.brands.map((b) => this._brandItem(b, menu.id))}
              </aside>
            `
          : content
      }
    </div>`;
  }

  _trigger(menu, index) {
    const open = this._open === menu.id;
    return html`<button
      id="trigger-${menu.id}"
      class="trigger"
      part="menu-trigger"
      role="menuitem"
      tabindex=${index === this._activeIndex ? '0' : '-1'}
      aria-expanded=${open ? 'true' : 'false'}
      aria-haspopup="true"
      aria-controls="panel-${menu.id}"
      data-track-entity=${`navigation.header.dropdown_toggle.${menu.id}.click`}
      @click=${() => this._toggle(menu.id)}
    >
      <span>${this._t(menu)}</span>
      <svg
        class="chevron"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="currentColor"
      >
        <path
          d="M10.375,3.219,6,6.719l-4.375-3.5A1,1,0,1,0,.375,4.781l5,4a1,1,0,0,0,1.25,0l5-4a1,1,0,0,0-1.25-1.562Z"
        ></path>
      </svg>
    </button>`;
  }

  // Default logo, rendered as slot fallback content.
  //
  // This is deliberately NOT left to each host. The Postman mark is the most
  // universal part of the navbar, and an empty slot means every consumer has
  // to remember to fill it — which is exactly the per-property drift this
  // component exists to remove. Both the www-next and blog integrations
  // shipped without a logo before this was added, and their unit tests passed
  // because the tests projected one.
  //
  // Hosts that need something different (a co-branded lockup, a different
  // link target) still override it by projecting into slot="logo".
  _logo() {
    // Resolves like any other internal link: root-relative on www-family
    // hosts, absolute elsewhere, locale-prefixed where the locale localizes
    // pages.
    const link = { type: 'internal', path: '/', i18n: true };
    // nav.homeLabel does exist in global.json; it simply was not a DECLARED key,
    // so the generator never pulled its translations. Declared in UI_KEYS as of
    // v1.0.6, so this now localizes.
    const label = resolveCopy('nav.homeLabel', 'Home', this._copyMap);
    // Dark theme swaps in a full white wordmark for the mobile hamburger bar
    // below 1150px — the icon-only mark alone reads as a stray dot against the
    // near-black bar (mirrors www-next's NavLogos.jsx). Only fetched/rendered
    // when dark, since the wordmark SVG is hardcoded white and would be
    // invisible on every other page's white bar.
    const dark = this.theme === 'dark';
    return html`<a class="logo" part="logo" href=${resolveHref(link, this.ctx)} aria-label=${label}>
      <!-- alt="" because the anchor carries the accessible name; a filled alt
           here would make screen readers announce it twice. -->
      ${
        dark
          ? html`<img
              class="logo-wordmark"
              src="https://voyager.postman.com/uploads/postman-postman-logo-full-white-1-2026-09-29-2042.svg"
              alt=""
              width="94"
              height="28"
            />`
          : nothing
      }
      <img
        class="logo-mark"
        src="https://voyager.postman.com/logo/postman-logo-icon-orange.svg"
        alt=""
        width="32"
        height="32"
      />
    </a>`;
  }

  /**
   * Default account controls, rendered as `account` / `account-mobile` slot
   * fallback.
   *
   * Mirrors www-next's LoginCheck exactly: Contact Sales is always shown, then
   * Sign In + Sign Up when signed out, or Launch Postman when signed in.
   *
   * `signed-in` was previously declared in CONTRACT.md and typed here but never
   * read, so a host wiring its auth state to it got silence. Consumers with
   * their own auth plumbing (www-next rewrites identity hrefs per environment;
   * the blog carries Pingdom check hooks) still override via the slots — this is
   * the zero-config default for hosts that just need working buttons.
   *
   * Deliberately NO environment detection here: the component never rewrites
   * hosts. A consumer needing beta/stage identity URLs fills the slot instead.
   */
  _account(mobile = false) {
    const t = (key, en) => resolveCopy(key, en, this._copyMap);
    // Mirrors www-next's LoginCheck `cdp()` helper exactly — a hand-built JSON string
    // (not JSON.stringify) forwarded to Segment/CDP as a "CTA Clicked" event. Only the
    // two identity CTAs (Contact Sales, Sign Up) carry it there; Sign In and Launch
    // Postman rely on data-track-entity (pmt/SCALP) alone.
    const cdp = (destination) =>
      `{"event":"CTA Clicked","type":"button","location":"head","destination":"${destination}"}`;
    const link = (cls, href, label, trackEntity, cdpBlob) =>
      html`<a
        class="acct ${cls}"
        part="account-link"
        href=${href}
        data-track-entity=${trackEntity}
        data-cdp=${cdpBlob ?? nothing}
        >${label}</a
      >`;

    const contactSalesHref = 'https://www.postman.com/company/contact-sales/';
    const signUpHref = 'https://identity.getpostman.com/signup';

    return html`<div class="acct-group ${mobile ? 'acct-mobile' : ''}">
      ${link(
        'acct-secondary',
        contactSalesHref,
        t('nav.contactSales', 'Contact Sales'),
        'navigation.header.contact_sales.click',
        cdp(contactSalesHref)
      )}
      ${
        this.signedIn
          ? [
              link(
                'acct-primary',
                'https://go.postman.co/home',
                t('nav.launchPostman', 'Launch Postman'),
                'navigation.header.launch_postman.click'
              ),
            ]
          : [
              link(
                'acct-secondary',
                'https://identity.getpostman.com/login',
                t('nav.signIn', 'Sign In'),
                'navigation.header.sign_in.click'
              ),
              link(
                'acct-primary',
                signUpHref,
                t('nav.signUp', 'Sign Up for Free'),
                'navigation.header.sign_up_for_free.click',
                cdp(signUpHref)
              ),
            ]
      }
    </div>`;
  }

  /**
   * Keep the open mega-menu inside the viewport.
   *
   * Panels are `position: absolute; left: -14px; width: max-content`, so they
   * size to their content and hang off the trigger. Resources is the rightmost
   * trigger, and when it gained the Tools column in v1.0.2 it grew from 762px to
   * 942px — enough to run 108px past the right edge at a 1369px viewport, which
   * clipped the whole POSTMAN column. The old marketing-site navbar had a
   * "flexible translateX anchor" for this; the component had nothing.
   *
   * Measured and corrected after render rather than in CSS because the overflow
   * depends on the trigger's position AND the panel's content width, neither of
   * which CSS can compare to the viewport.
   */
  updated() {
    const panel = this._open ? this.renderRoot.querySelector(`#panel-${this._open}`) : null;

    // Always clear first: a stale shift would misplace the next panel opened.
    for (const el of this.renderRoot.querySelectorAll('.panel')) {
      if (el !== panel) el.style.transform = '';
    }
    if (!panel || typeof window === 'undefined') return;
    // Only floating panels can hang off an edge. In the drawer they are static
    // and full-width, and measuring one mid-slide would shift it for no reason —
    // the site's own clamp bails below the breakpoint for the same reason.
    if (this._desktopQuery && !this._desktopQuery.matches) return;

    panel.style.transform = '';
    const gutter = 16;
    const rect = panel.getBoundingClientRect();
    const overflowRight = rect.right - (window.innerWidth - gutter);
    if (overflowRight > 0) {
      // Never shift so far that the left edge goes off-screen instead.
      const shift = Math.min(overflowRight, Math.max(0, rect.left - gutter));
      if (shift > 0) panel.style.transform = `translateX(${-Math.ceil(shift)}px)`;
    }
  }

  render() {
    return html`
      <nav part="navbar" aria-label="Primary">
        <div class="bar">
          <div class="bar-left">
            <slot name="logo">${this._logo()}</slot>
            <!-- The mobile drawer, and on desktop just a wrapper around the
                 menubar. tabindex="-1" so opening it can move focus here, the
                 way the marketing site focuses its panel. Deliberately NOT
                 role="navigation": that would add a second nav landmark inside
                 the outer one at every width, including desktop, where this
                 element is only a layout wrapper. -->
            <div
              id="mobile-panel"
              class="drawer"
              part="mobile-panel"
              tabindex="-1"
              data-open=${this._mobileOpen ? 'true' : 'false'}
              @click=${(e) => this._onDrawerClick(e)}
            >
              <div class="drawer-scroll">
                <ul
                  id="menubar"
                  class="menubar"
                  part="menubar"
                  role="menubar"
                  aria-label="Primary"
                  aria-orientation="horizontal"
                  @keydown=${(e) => this._onMenubarKeydown(e)}
                >
                  ${navData.map((menu, i) =>
                    menu.dropdown
                      ? html`<li class="item" role="none">
                          ${this._trigger(menu, i)}${this._panel(menu)}
                        </li>`
                      : html`<li class="item" role="none">${this._link(menu, 'top-link', i)}</li>`
                  )}
                </ul>
                <div class="drawer-account">
                  <slot name="account-mobile">${this._account(true)}</slot>
                </div>
                <div class="drawer-local">
                  <slot name="local-nav-mobile"></slot>
                </div>
              </div>
            </div>
          </div>
          <div class="bar-right">
            <div class="actions" @click=${() => this._onBarControlsClick()}>
              <slot name="search"></slot>
              <slot name="local-nav"></slot>
              <slot name="account">${this._account()}</slot>
            </div>
            <button
              type="button"
              class="hamburger"
              part="hamburger"
              aria-label=${resolveCopy('nav.toggleMenu', 'Toggle navigation menu', this._copyMap)}
              aria-expanded=${this._mobileOpen ? 'true' : 'false'}
              aria-controls="mobile-panel"
              aria-haspopup="menu"
              @click=${() => this._toggleDrawer()}
            >
              <span class="ham-box" aria-hidden="true">
                <span class="ham-lines">
                  <span class="ham-bar"></span>
                  <span class="ham-bar"></span>
                  <span class="ham-bar"></span>
                </span>
              </span>
            </button>
          </div>
        </div>
      </nav>
    `;
  }
}

// Guarded rather than a bare define(): a page that loads BOTH bundles — the
// hydrate one for a prerendered navbar and the client one for a footer, which is
// exactly what a consumer adopting them separately ends up with — would
// otherwise throw NotSupportedError on the second define and abort that whole
// bundle, taking the element it was loaded for with it.
//
// First definition wins. If the two bundles are different VERSIONS the older may
// win silently, so consumers should still load one; this makes the mistake
// harmless rather than fatal.
if (!customElements.get('postman-navbar')) {
  customElements.define('postman-navbar', PostmanNavbar);
}
